import { useCallback, useState } from "react";
import { UseFormReturn } from "react-hook-form";

import { validateDraftForReview } from "@RequestFormV2/api/draftApi";
import { useRequestFormMode } from "@RequestFormV2/context/requestFormMode";
import { useRequestFormSaveOrNull } from "@RequestFormV2/context/save";
import { useRequestFormDraftId } from "@RequestFormV2/hooks/useRequestFormDraftId";
import { buildErroredSectionsFromApiError } from "@RequestFormV2/utils/buildErroredSectionsFromApiError";
import type { ReviewFieldErrorSnapshot, ValidateOnReviewResult } from "@RequestFormV2/validation/validateOnReview";
import errorToast from "helpers/errorToast";
import { resolveServerErrors } from "helpers/requestFormHelper";
import { flattenApiErrors, trackQuoteFormErrorLogged } from "@RequestFormV2/formEvents/requestFormAnalytics";

export type RequestFormReviewResult =
  | { success: true }
  | {
      success: false;
      erroredSections?: string[];
      fieldErrors?: ReviewFieldErrorSnapshot[];
    };

type UseRequestFormReviewOptions = {
  runReviewValidation: () => Promise<ValidateOnReviewResult> | ValidateOnReviewResult;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useRequestFormReview(methods: UseFormReturn<any>, { runReviewValidation }: UseRequestFormReviewOptions) {
  const mode = useRequestFormMode();
  const draftId = useRequestFormDraftId();
  const saveContext = useRequestFormSaveOrNull();
  const { getValues, setError } = methods;
  const [isReviewValidating, setIsReviewValidating] = useState(false);

  const handleReview = useCallback(async (): Promise<RequestFormReviewResult> => {
    const { isValid, erroredSections, fieldErrors } = await runReviewValidation();

    if (!isValid) {
      return { success: false, erroredSections, fieldErrors };
    }

    if (!draftId) {
      return { success: false };
    }

    setIsReviewValidating(true);
    try {
      const saved = (await saveContext?.flushSave()) ?? true;
      if (!saved) {
        errorToast(undefined, "Unable to save draft. Please try again.");
        return { success: false };
      }
      await validateDraftForReview(draftId, mode);
      return { success: true };
    } catch (err) {
      resolveServerErrors(err, { ...getValues() }, setError);
      trackQuoteFormErrorLogged(flattenApiErrors(err));
      errorToast(err);
      return {
        success: false,
        erroredSections: buildErroredSectionsFromApiError(err, mode),
      };
    } finally {
      setIsReviewValidating(false);
    }
  }, [draftId, saveContext, runReviewValidation, getValues, setError, mode]);

  return { handleReview, isReviewValidating };
}
